import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { ScreenLayout, FooterMeta, PageHeader, StyledButton } from '../shared';
import { isDedicated } from '../../../../../shared/styles';

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledBanner = styled.div`
  margin: 1rem 1rem 0 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9e7e4;
`;

const StyledBannerTitle = styled.h2`
  margin: 0 0 0.25rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #232323;
`;

const StyledBannerText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #232323;
  line-height: 1.5rem;
`;

const StyledBannerEmphasis = styled.b`
  font-weight: 600;
`;

const StyledSection = styled.section`
  padding: 1rem 0;
  margin: 0 1rem;

  & + & {
    border-top: 1px solid #e9e7e4;
  }
`;

const StyledSectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #232323;
`;

const StyledItem = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: flex-start;

  & + & {
    margin-top: 1rem;
  }
`;

const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StyledItemContent = styled.span`
  font-size: 0.9375rem;
  color: #232323;
`;

const StyledItemTitle = styled.b`
  font-weight: 600;
`;

const StyledItemDescription = styled.span`
  line-height: 1.4;
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-top: 1px solid #e8e9eb;
`;

const StyledCTA = styled(StyledButton)`
  width: calc(100% - 2rem);
  margin: 0.75rem 1rem 0 1rem;
  padding: 0.75rem;
  border: none;
  border-radius: 0.625rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:focus-visible {
    outline: 2px solid var(--rg-primary-color, #232323);
    outline-offset: 2px;
  }
`;

function ChatIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.625 9H5.6325M9 9H9.0075M12.375 9H12.3825M9 15.75C12.7279 15.75 15.75 12.7279 15.75 9C15.75 5.27208 12.7279 2.25 9 2.25C5.27208 2.25 2.25 5.27208 2.25 9C2.25 9.89786 2.4253 10.7548 2.74355 11.5384C2.80446 11.6884 2.83491 11.7633 2.8485 11.824C2.86179 11.8833 2.86665 11.9271 2.86666 11.9879C2.86666 12.05 2.85539 12.1177 2.83283 12.253L2.3881 14.9214C2.34152 15.2009 2.31824 15.3406 2.36157 15.4416C2.3995 15.53 2.46996 15.6005 2.55839 15.6384C2.65942 15.6818 2.79914 15.6585 3.07857 15.6119L5.74699 15.1672C5.88231 15.1446 5.94998 15.1333 6.0121 15.1333C6.07286 15.1334 6.11675 15.1382 6.17604 15.1515C6.23666 15.1651 6.31164 15.1955 6.46162 15.2565C7.24523 15.5747 8.10214 15.75 9 15.75ZM6 9C6 9.20711 5.83211 9.375 5.625 9.375C5.41789 9.375 5.25 9.20711 5.25 9C5.25 8.79289 5.41789 8.625 5.625 8.625C5.83211 8.625 6 8.79289 6 9ZM9.375 9C9.375 9.20711 9.20711 9.375 9 9.375C8.79289 9.375 8.625 9.20711 8.625 9C8.625 8.79289 8.79289 8.625 9 8.625C9.20711 8.625 9.375 8.79289 9.375 9ZM12.75 9C12.75 9.20711 12.5821 9.375 12.375 9.375C12.1679 9.375 12 9.20711 12 9C12 8.79289 12.1679 8.625 12.375 8.625C12.5821 8.625 12.75 8.79289 12.75 9Z"
        stroke="#232323"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LeaseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.4766 14.7209H8.47658M0.601562 15.0959L4.76351 13.4952C5.02972 13.3928 5.16282 13.3416 5.28735 13.2748C5.39796 13.2154 5.50341 13.1469 5.60259 13.0699C5.71425 12.9832 5.81509 12.8824 6.01677 12.6807L14.4766 4.22093C15.305 3.3925 15.305 2.04936 14.4766 1.22093C13.6482 0.392503 12.305 0.392502 11.4766 1.22093L3.01677 9.68072C2.81509 9.8824 2.71425 9.98324 2.62761 10.0949C2.55064 10.1941 2.48211 10.2995 2.42274 10.4101C2.35589 10.5347 2.3047 10.6678 2.20231 10.934L0.601562 15.0959ZM0.601562 15.0959L2.14515 11.0826C2.25561 10.7955 2.31084 10.6519 2.40557 10.5861C2.48835 10.5286 2.59079 10.5069 2.68978 10.5258C2.80306 10.5474 2.91185 10.6562 3.12942 10.8738L4.82374 12.5681C5.04132 12.7857 5.1501 12.8945 5.17174 13.0077C5.19064 13.1067 5.1689 13.2092 5.11142 13.2919C5.04565 13.3867 4.90205 13.4419 4.61487 13.5524L0.601562 15.0959Z"
        stroke="#232323"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.75 15.75V10.2C6.75 9.77996 6.75 9.56994 6.83175 9.40951C6.90365 9.26839 7.01839 9.15365 7.15951 9.08175C7.31994 9 7.52996 9 7.95 9H10.05C10.47 9 10.6801 9 10.8405 9.08175C10.9816 9.15365 11.0964 9.26839 11.1683 9.40951C11.25 9.56994 11.25 9.77996 11.25 10.2V15.75M1.5 7.125L8.28 2.04C8.5382 1.84635 8.66729 1.74953 8.80908 1.71221C8.93423 1.67926 9.06578 1.67926 9.19093 1.71221C9.33271 1.74953 9.46181 1.84635 9.72 2.04L16.5 7.125M3 6V13.35C3 14.1901 3 14.6101 3.16349 14.931C3.3073 15.2132 3.53677 15.4427 3.81902 15.5865C4.13988 15.75 4.55992 15.75 5.4 15.75H12.6C13.4401 15.75 13.8601 15.75 14.181 15.5865C14.4632 15.4427 14.6927 15.2132 14.8365 14.931C15 14.6101 15 14.1901 15 13.35V6L10.44 2.58C9.92362 2.19271 9.66542 1.99906 9.38185 1.92442C9.13155 1.85853 8.86846 1.85853 8.61815 1.92442C8.33459 1.99906 8.07639 2.19271 7.56 2.58L3 6Z"
        stroke="#232323"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.5 6.75H8.625C8.00368 6.75 7.5 7.25368 7.5 7.875C7.5 8.49632 8.00368 9 8.625 9H9.375C9.99632 9 10.5 9.50368 10.5 10.125C10.5 10.7463 9.99632 11.25 9.375 11.25H7.5M9 6V6.75M9 11.25V12M13.5 9H13.5075M4.5 9H4.5075M1.5 6.15L1.5 11.85C1.5 12.6901 1.5 13.1101 1.66349 13.431C1.8073 13.7132 2.03677 13.9427 2.31901 14.0865C2.63988 14.25 3.05992 14.25 3.9 14.25L14.1 14.25C14.9401 14.25 15.3601 14.25 15.681 14.0865C15.9632 13.9427 16.1927 13.7132 16.3365 13.431C16.5 13.1101 16.5 12.6901 16.5 11.85V6.15C16.5 5.30992 16.5 4.88988 16.3365 4.56902C16.1927 4.28677 15.9632 4.0573 15.681 3.91349C15.3601 3.75 14.9401 3.75 14.1 3.75L3.9 3.75C3.05992 3.75 2.63988 3.75 2.31901 3.91349C2.03677 4.0573 1.8073 4.28677 1.66349 4.56901C1.5 4.88988 1.5 5.30992 1.5 6.15ZM13.875 9C13.875 9.20711 13.7071 9.375 13.5 9.375C13.2929 9.375 13.125 9.20711 13.125 9C13.125 8.79289 13.2929 8.625 13.5 8.625C13.7071 8.625 13.875 8.79289 13.875 9ZM4.875 9C4.875 9.20711 4.70711 9.375 4.5 9.375C4.29289 9.375 4.125 9.20711 4.125 9C4.125 8.79289 4.29289 8.625 4.5 8.625C4.70711 8.625 4.875 8.79289 4.875 9Z"
        stroke="#232323"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_8050_3119)">
        <path
          d="M9 1.5V2.25M2.25 9H1.5M4.125 4.125L3.67493 3.67493M13.875 4.125L14.3252 3.67493M16.5 9H15.75M7.5 10.125H10.5M9 10.125V13.875M11.625 12.6555C12.7606 11.8386 13.5 10.5056 13.5 9C13.5 6.51472 11.4853 4.5 9 4.5C6.51472 4.5 4.5 6.51472 4.5 9C4.5 10.5056 5.23945 11.8386 6.375 12.6555V14.1C6.375 14.9401 6.375 15.3601 6.53849 15.681C6.6823 15.9632 6.91177 16.1927 7.19401 16.3365C7.51488 16.5 7.93492 16.5 8.775 16.5H9.225C10.0651 16.5 10.4851 16.5 10.806 16.3365C11.0882 16.1927 11.3177 15.9632 11.4615 15.681C11.625 15.3601 11.625 14.9401 11.625 14.1V12.6555Z"
          stroke="#232323"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_8050_3119">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_8050_3123)">
        <path
          d="M5.625 9L7.875 11.25L12.375 6.75M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z"
          stroke="#232323"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_8050_3123">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function CoinIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_8050_3127)">
        <path
          d="M9 4.5V16.5M9 4.5H6.34821C5.95749 4.5 5.58278 4.34196 5.30649 4.06066C5.03021 3.77936 4.875 3.39782 4.875 3C4.875 2.60218 5.03021 2.22064 5.30649 1.93934C5.58278 1.65804 5.95749 1.5 6.34821 1.5C8.41071 1.5 9 4.5 9 4.5ZM9 4.5H11.6518C12.0425 4.5 12.4172 4.34196 12.6935 4.06066C12.9698 3.77936 13.125 3.39782 13.125 3C13.125 2.60218 12.9698 2.22064 12.6935 1.93934C12.4172 1.65804 12.0425 1.5 11.6518 1.5C9.58929 1.5 9 4.5 9 4.5ZM15 8.25V14.1C15 14.9401 15 15.3601 14.8365 15.681C14.6927 15.9632 14.4632 16.1927 14.181 16.3365C13.8601 16.5 13.4401 16.5 12.6 16.5L5.4 16.5C4.55992 16.5 4.13988 16.5 3.81901 16.3365C3.53677 16.1927 3.3073 15.9632 3.16349 15.681C3 15.3601 3 14.9401 3 14.1V8.25M1.5 5.7L1.5 7.05C1.5 7.47004 1.5 7.68006 1.58175 7.84049C1.65365 7.98161 1.76839 8.09635 1.90951 8.16825C2.06994 8.25 2.27996 8.25 2.7 8.25L15.3 8.25C15.72 8.25 15.9301 8.25 16.0905 8.16825C16.2316 8.09635 16.3463 7.98161 16.4183 7.84049C16.5 7.68006 16.5 7.47004 16.5 7.05V5.7C16.5 5.27996 16.5 5.06994 16.4183 4.90951C16.3463 4.76839 16.2316 4.65365 16.0905 4.58175C15.9301 4.5 15.72 4.5 15.3 4.5L2.7 4.5C2.27996 4.5 2.06994 4.5 1.90951 4.58175C1.76839 4.65365 1.65365 4.76839 1.58175 4.90951C1.5 5.06994 1.5 5.27996 1.5 5.7Z"
          stroke="#232323"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_8050_3127">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9 11.625H5.625C4.57833 11.625 4.05499 11.625 3.62914 11.7542C2.67034 12.045 1.92003 12.7953 1.62918 13.7541C1.5 14.18 1.5 14.7033 1.5 15.75M12 13.5L13.5 15L16.5 12M10.875 5.625C10.875 7.48896 9.36396 9 7.5 9C5.63604 9 4.125 7.48896 4.125 5.625C4.125 3.76104 5.63604 2.25 7.5 2.25C9.36396 2.25 10.875 3.76104 10.875 5.625Z"
        stroke="#232323"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_8054_3153)">
        <path
          d="M9 11.25C6.51472 11.25 4.5 9.23528 4.5 6.75V2.58333C4.5 2.27295 4.5 2.11775 4.54523 1.99348C4.62105 1.78516 4.78516 1.62105 4.99348 1.54523C5.11775 1.5 5.27295 1.5 5.58333 1.5H12.4167C12.7271 1.5 12.8822 1.5 13.0065 1.54523C13.2148 1.62105 13.3789 1.78516 13.4548 1.99348C13.5 2.11775 13.5 2.27295 13.5 2.58333V6.75C13.5 9.23528 11.4853 11.25 9 11.25ZM9 11.25V13.5M13.5 3H15.375C15.7245 3 15.8992 3 16.037 3.05709C16.2208 3.13321 16.3668 3.27922 16.4429 3.46299C16.5 3.60082 16.5 3.77554 16.5 4.125V4.5C16.5 5.19748 16.5 5.54622 16.4233 5.83234C16.2153 6.6088 15.6088 7.21528 14.8323 7.42333C14.5462 7.5 14.1975 7.5 13.5 7.5M4.5 3H2.625C2.27554 3 2.10082 3 1.96299 3.05709C1.77922 3.13321 1.63321 3.27922 1.55709 3.46299C1.5 3.60082 1.5 3.77554 1.5 4.125V4.5C1.5 5.19748 1.5 5.54622 1.57667 5.83234C1.78472 6.6088 2.3912 7.21528 3.16766 7.42333C3.45378 7.5 3.80252 7.5 4.5 7.5M5.58333 16.5H12.4167C12.6008 16.5 12.75 16.3508 12.75 16.1667C12.75 14.6939 11.5561 13.5 10.0833 13.5H7.91667C6.44391 13.5 5.25 14.6939 5.25 16.1667C5.25 16.3508 5.39924 16.5 5.58333 16.5Z"
          stroke="#232323"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_8054_3153">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.75 8.62478L8.25 10.1248L11.625 6.74978M15 8.99978C15 12.6811 10.9845 15.3586 9.52349 16.2109C9.35745 16.3078 9.27443 16.3562 9.15726 16.3814C9.06633 16.4009 8.93367 16.4009 8.84274 16.3814C8.72557 16.3562 8.64255 16.3078 8.47651 16.2109C7.01547 15.3586 3 12.6811 3 8.99978V5.41298C3 4.81335 3 4.51353 3.09807 4.25581C3.18471 4.02814 3.32549 3.82499 3.50824 3.66393C3.71512 3.48161 3.99585 3.37634 4.5573 3.16579L8.57865 1.65779C8.73457 1.59932 8.81253 1.57008 8.89274 1.55849C8.96388 1.54821 9.03612 1.54821 9.10726 1.55849C9.18747 1.57008 9.26543 1.59932 9.42135 1.65779L13.4427 3.16579C14.0042 3.37634 14.2849 3.48161 14.4918 3.66393C14.6745 3.82499 14.8153 4.02814 14.9019 4.25581C15 4.51353 15 4.81335 15 5.41298V8.99978Z"
        stroke="#232323"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type ItemProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

function InfoItem({ icon, title, description }: Readonly<ItemProps>) {
  return (
    <StyledItem>
      <StyledIconWrapper>{icon}</StyledIconWrapper>
      <StyledItemContent>
        <StyledItemTitle>{title}: </StyledItemTitle>
        <StyledItemDescription>{description}</StyledItemDescription>
      </StyledItemContent>
    </StyledItem>
  );
}

export function HowThisWorksPage() {
  const navigate = useNavigate();
  const listing = globalThis.xprops.listing;
  const hasReward = listing.resident_reward && listing.reward_amount > 0;

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleClose = useCallback(() => {
    globalThis.xprops?.onToggleMessenger?.();
  }, []);

  const allowClose = useMemo(
    () => !isDedicated(globalThis.xprops.renderAs),
    []
  );

  return (
    <ScreenLayout
      header={
        <PageHeader
          title="How it works"
          onBack={handleBack}
          onClose={allowClose ? handleClose : undefined}
        />
      }
      footer={
        <StyledFooter>
          <StyledCTA type="button" onClick={handleBack}>
            Start a conversation
          </StyledCTA>
          <FooterMeta />
        </StyledFooter>
      }
    >
      <StyledBody>
        {hasReward && (
          <StyledBanner>
            <StyledBannerTitle>
              Resident Ambassador move-in bonuses &amp; resident chats &nbsp;
            </StyledBannerTitle>
            <StyledBannerText>
              Resident Ambassador lets you &nbsp;
              <StyledBannerEmphasis>
                chat with real residents
              </StyledBannerEmphasis>
              &nbsp; to get honest insights before you lease—and some
              communities even offer &nbsp;
              <StyledBannerEmphasis>move-in bonuses</StyledBannerEmphasis>
              &nbsp; as a reward.
            </StyledBannerText>
          </StyledBanner>
        )}

        <StyledSection>
          <StyledSectionTitle>How it works</StyledSectionTitle>
          <InfoItem
            icon={<ChatIcon />}
            title="Chat First"
            description="Message a resident and get a reply."
          />
          <InfoItem
            icon={<LeaseIcon />}
            title="Apply & Lease"
            description="Submit your application and sign a lease (usually 12+ months)."
          />
          <InfoItem
            icon={<HomeIcon />}
            title="Move In"
            description="Stay the required minimum (often 30–90 days)."
          />
          {hasReward && (
            <InfoItem
              icon={<GiftIcon />}
              title="Get Your Bonus"
              description="Community verifies and pays directly (rent credit, gift card, cash, etc.)."
            />
          )}
        </StyledSection>

        <StyledSection>
          <StyledSectionTitle>Why chat with residents?</StyledSectionTitle>
          <InfoItem
            icon={<LightbulbIcon />}
            title="Real Insights"
            description="Hear firsthand about life in the community."
          />
          <InfoItem
            icon={<CheckCircleIcon />}
            title="Make Better Decisions"
            description="Avoid surprises and find the right fit."
          />
          <InfoItem
            icon={<CoinIcon />}
            title="Earn Rewards"
            description="Move-in bonuses may be available in participating communities."
          />
        </StyledSection>

        <StyledSection>
          <StyledSectionTitle>About Ambassadors</StyledSectionTitle>
          <InfoItem
            icon={<PeopleIcon />}
            title="Who They Are"
            description="Volunteers—current residents who answer questions."
          />
          <InfoItem
            icon={<StarIcon />}
            title="Rewards"
            description="Certain communities may thank ambassadors with incentives if chats lead to leases."
          />
          <InfoItem
            icon={<ShieldCheckIcon />}
            title="Authentic Feedback"
            description="Verified residents; no bots or scripts."
          />
        </StyledSection>
      </StyledBody>
    </ScreenLayout>
  );
}
